import React, { useEffect, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { CustomSelect } from '@/components';
import { uploadWhatsAppFlowMedia } from '../../whatsapp-flows/api';
import { BLOCKS } from './EmailBlockToolbar';
import {
  SOCIAL_PLATFORM_OPTIONS,
  getSocialIconUrl,
  getSocialPlaceholder,
} from './socialIcons';

const AlignStyle = Quill.import('attributors/style/align');
const BackgroundStyle = Quill.import('attributors/style/background');
const ColorStyle = Quill.import('attributors/style/color');
const DirectionStyle = Quill.import('attributors/style/direction');
const FontStyle = Quill.import('attributors/style/font');
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['12px', '14px', '16px', '18px', '24px', '32px'];
Quill.register(AlignStyle, true);
Quill.register(BackgroundStyle, true);
Quill.register(ColorStyle, true);
Quill.register(DirectionStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);

const QUILL_MODULES = {
  toolbar: [
    [{ size: ['12px', '14px', '16px', '18px', '24px', '32px'] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { align: [] }],
    [{ header: [1, 2, 3, false] }, 'strike', { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, 'link', 'clean'],
  ],
};

const QUILL_CSS = `
.email-quill .ql-toolbar.ql-snow {
  border: none; border-bottom: 1px solid #e5e7eb; padding: 4px 6px; background: #fff;
  display: flex; flex-wrap: nowrap; gap: 2px; overflow-x: auto; overflow-y: hidden;
  -webkit-overflow-scrolling: touch; touch-action: pan-x; max-width: 100%;
}
.email-quill .ql-formats { flex-shrink: 0; }
.email-quill .ql-container.ql-snow { border: none; overflow: visible; }
.email-quill .ql-editor { min-height: 140px; max-height: 160px; overflow-y: auto; font-size: 14px; }
.email-quill .ql-picker { position: static; }
.email-quill .ql-picker-options {
  max-height: 200px;
  overflow-y: auto;
  overscroll-behavior: contain;
  z-index: 80;
}
.email-quill .ql-formats { margin-right: 2px !important; flex-shrink: 0; }
.email-quill:not(.show-advanced) .ql-header,
.email-quill:not(.show-advanced) .ql-strike,
.email-quill:not(.show-advanced) .ql-background,
.email-quill:not(.show-advanced) .ql-list,
.email-quill:not(.show-advanced) .ql-link,
.email-quill:not(.show-advanced) .ql-clean { display: none !important; }
.email-quill .ql-snow .ql-picker.ql-size { width: 68px; }
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: '12px'; }
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: '14px'; }
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: '16px'; }
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: '18px'; }
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before { content: '24px'; }
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
.email-quill .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: '32px'; }
`;

const SWATCHES = [
  '#ffffff',
  '#f3f4f6',
  '#1f2937',
  '#50AB3D',
  '#1E6555',
  '#3b82f6',
  '#f59e0b',
  '#ef4444',
];

const SPACE_PRESETS = [
  { id: '8px', label: 'Small' },
  { id: '16px', label: 'Medium' },
  { id: '24px', label: 'Large' },
];

const WIDTHS = [
  { value: '100%', label: 'Full width — 100%' },
  { value: '80%', label: 'Large — 80%' },
  { value: '50%', label: 'Medium — 50%' },
  { value: '150px', label: 'Small — 150px' },
  { value: '80px', label: 'Extra small — 80px' },
];

const CORNERS = [
  { value: '0px', label: 'Square' },
  { value: '4px', label: 'Subtle' },
  { value: '8px', label: 'Rounded' },
  { value: '12px', label: '12px' },
  { value: '999px', label: 'Pill' },
];

const SPACER_HEIGHTS = [
  { value: '16px', label: 'Small' },
  { value: '24px', label: 'Medium' },
  { value: '40px', label: 'Large' },
  { value: '60px', label: '60px' },
];

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30';
const labelClass = 'mb-1 block text-[13px] font-medium text-gray-700';
const selectBtn =
  'w-full !h-9 flex items-center justify-between bg-white border border-gray-200 rounded-md !px-2.5 text-sm';

function isLight(hex) {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return true;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 180;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className={labelClass}>{label}</span>
      {children}
    </label>
  );
}

function Swatches({ value, onChange, presets = SWATCHES }) {
  const safe = /^#[0-9a-fA-F]{6}$/i.test(value || '') ? value : '#50AB3D';
  return (
    <div className="flex max-w-full items-center gap-1.5 overflow-x-auto touch-pan-x">
      {presets.map((preset) => {
        const on = (value || '').toLowerCase() === preset.toLowerCase();
        return (
          <button
            key={preset}
            type="button"
            title={preset}
            aria-label={preset}
            aria-pressed={on}
            onClick={() => onChange(preset)}
            style={{ backgroundColor: preset }}
            className={`relative h-7 w-7 shrink-0 rounded-md border border-gray-300 ${
              on ? 'ring-2 ring-primary ring-offset-1' : ''
            }`}
          >
            {on && (
              <svg
                viewBox="0 0 16 16"
                className={`absolute inset-0 m-auto h-3.5 w-3.5 ${
                  isLight(preset) ? 'text-gray-900' : 'text-white'
                }`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
              >
                <path d="M3.5 8.2 6.4 11l6.1-6.2" />
              </svg>
            )}
          </button>
        );
      })}
      <label
        className="relative h-7 w-7 shrink-0 cursor-pointer overflow-hidden rounded-md border border-gray-300"
        title="Custom color"
      >
        <span className="sr-only">Custom color</span>
        <input
          type="color"
          value={safe}
          onChange={(e) => onChange(e.target.value)}
          className="absolute -left-1 -top-1 h-10 w-10 cursor-pointer border-0 p-0"
        />
      </label>
    </div>
  );
}

function AlignRow({ value = 'left', onChange }) {
  const options = [
    ['left', 'Left', 'M4 6h16M4 12h10M4 18h14'],
    ['center', 'Center', 'M4 6h16M7 12h10M5 18h14'],
    ['right', 'Right', 'M4 6h16M10 12h10M6 18h14'],
  ];
  return (
    <div className="grid grid-cols-3 gap-1">
      {options.map(([id, name, d]) => {
        const on = value === id;
        return (
          <button
            key={id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(id)}
            className={`flex h-9 items-center justify-center gap-1 rounded-md border text-[13px] font-medium ${
              on
                ? 'border-primary bg-surface-mint/50 text-primary'
                : 'border-gray-200 text-gray-600'
            }`}
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" d={d} />
            </svg>
            {name}
          </button>
        );
      })}
    </div>
  );
}

function SpaceAround({ content, onChange }) {
  const pt = content.paddingTop ?? content.padding ?? '16px';
  const pr = content.paddingRight ?? content.padding ?? '16px';
  const pb = content.paddingBottom ?? content.padding ?? '16px';
  const pl = content.paddingLeft ?? content.padding ?? '16px';
  const uniform = pt === pr && pr === pb && pb === pl;
  const preset = uniform ? SPACE_PRESETS.find((p) => p.id === pt) : null;

  const setAll = (val) =>
    onChange({
      paddingTop: val,
      paddingRight: val,
      paddingBottom: val,
      paddingLeft: val,
      padding: val,
    });

  const setSide = (key, raw) => {
    const next = {
      ...content,
      paddingTop: pt,
      paddingRight: pr,
      paddingBottom: pb,
      paddingLeft: pl,
      [key]: raw,
    };
    delete next.padding;
    onChange(next);
  };

  return (
    <div>
      <span className={labelClass}>Space around block</span>
      <div className={`grid gap-1 ${preset ? 'grid-cols-3' : 'grid-cols-4'}`}>
        {SPACE_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            aria-pressed={preset?.id === p.id}
            onClick={() => setAll(p.id)}
            className={`h-9 rounded-md border text-[13px] font-medium ${
              preset?.id === p.id
                ? 'border-primary bg-surface-mint/50 text-primary'
                : 'border-gray-200 text-gray-600'
            }`}
          >
            {p.label}
          </button>
        ))}
        {!preset && (
          <span className="flex h-9 items-center justify-center rounded-md border border-primary bg-surface-mint/50 text-[13px] font-medium text-primary">
            Custom
          </span>
        )}
      </div>
      {!preset && (
        <div className="mt-2 grid grid-cols-4 gap-1">
          {[
            ['Top', 'paddingTop', pt],
            ['Right', 'paddingRight', pr],
            ['Bottom', 'paddingBottom', pb],
            ['Left', 'paddingLeft', pl],
          ].map(([name, key, val]) => (
            <label key={key} className="block">
              <span className="mb-0.5 block text-center text-[11px] text-gray-500">
                {name}
              </span>
              <input
                value={val}
                onChange={(e) => setSide(key, e.target.value)}
                className={`${inputClass} px-1 text-center`}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function Advanced({ open, onToggle, show, children }) {
  const hasBody = React.Children.toArray(children).some(Boolean);
  if (!show && !hasBody) return null;
  return (
    <div className="border-t border-gray-100 pt-2">
      <button
        type="button"
        className="text-[13px] font-medium text-gray-600 hover:text-primary"
        aria-expanded={open}
        onClick={onToggle}
      >
        {open ? 'Hide advanced' : 'Advanced'}
      </button>
      {open && hasBody && <div className="mt-2 space-y-3">{children}</div>}
    </div>
  );
}

function placeQuillMenus(root) {
  root.querySelectorAll('.ql-picker').forEach((picker) => {
    const menu = picker.querySelector('.ql-picker-options');
    const label = picker.querySelector('.ql-picker-label');
    if (!menu || !label) return;
    if (!picker.classList.contains('ql-expanded')) {
      menu.style.position = '';
      menu.style.top = '';
      menu.style.left = '';
      menu.style.bottom = '';
      menu.style.minWidth = '';
      menu.style.maxWidth = '';
      return;
    }
    const rect = label.getBoundingClientRect();
    const width = Math.max(rect.width, 112);
    const left = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - width - 8),
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    menu.style.position = 'fixed';
    menu.style.left = `${left}px`;
    menu.style.minWidth = `${width}px`;
    menu.style.maxWidth = `${window.innerWidth - 16}px`;
    menu.style.maxHeight = `${Math.max(80, Math.min(200, spaceBelow > 120 ? spaceBelow - 8 : rect.top - 8))}px`;
    menu.style.overflowY = 'auto';
    menu.style.zIndex = '80';
    if (spaceBelow < 120 && rect.top > spaceBelow) {
      menu.style.top = 'auto';
      menu.style.bottom = `${window.innerHeight - rect.top + 2}px`;
    } else {
      menu.style.bottom = 'auto';
      menu.style.top = `${rect.bottom + 2}px`;
    }
  });
}

function TextEditor({ blockId, html, onChange, advanced }) {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const place = () => placeQuillMenus(root);
    const onWheel = (event) => {
      const menu = event.target.closest?.('.ql-picker-options');
      if (!menu || !root.contains(menu)) return;
      event.preventDefault();
      event.stopPropagation();
      menu.scrollTop += event.deltaY;
    };
    const onClick = () => requestAnimationFrame(place);
    root.addEventListener('click', onClick);
    root.addEventListener('wheel', onWheel, { capture: true, passive: false });
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      root.removeEventListener('click', onClick);
      root.removeEventListener('wheel', onWheel, { capture: true });
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [blockId, advanced]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const active = document.activeElement;
      if (!active?.closest?.('.email-quill')) return;
      document.querySelector('.canvas-quill.is-active .ql-editor')?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [blockId]);

  return (
    <div
      ref={rootRef}
      className={`email-quill overflow-visible rounded-md border border-gray-200 ${advanced ? 'show-advanced' : ''}`}
    >
      <ReactQuill
        key={blockId}
        theme="snow"
        value={html || ''}
        onChange={(value, _delta, source) => {
          if (source !== 'user') return;
          if (value === (html || '')) return;
          onChange(value);
        }}
        modules={QUILL_MODULES}
      />
    </div>
  );
}

const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

function companyIdForUpload() {
  try {
    const stored = localStorage.getItem('user');
    if (stored) {
      const user = JSON.parse(stored);
      if (user?.company_id) return user.company_id;
    }
  } catch {
    /* ignore bad session */
  }
  return localStorage.getItem('company_id');
}

function checkImageFile(file) {
  if (!file) return 'Choose an image file.';
  const type = (file.type || '').toLowerCase();
  if (!IMAGE_TYPES.includes(type)) return 'Use a PNG, JPG, GIF, or WEBP image.';
  if (file.size > IMAGE_MAX_BYTES) return 'Image must be 10MB or smaller.';
  return '';
}

function ImageSource({ url, onChange }) {
  const inputRef = useRef(null);
  const [status, setStatus] = useState(url ? 'ready' : 'empty');
  const [progress, setProgress] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!url) setStatus('empty');
    else if (String(url).startsWith('blob:')) setStatus('error');
    else setStatus('loading');
  }, [url]);

  const upload = async (file) => {
    const problem = checkImageFile(file);
    if (problem) {
      setError(problem);
      return;
    }
    const companyId = companyIdForUpload();
    if (!companyId) {
      setError('No company is selected, so the image cannot be saved.');
      return;
    }
    setError('');
    setProgress(true);
    try {
      const response = await uploadWhatsAppFlowMedia({ companyId, file });
      const saved = response?.url;
      if (!saved || String(saved).startsWith('blob:')) {
        throw new Error('Upload did not return a saved image URL.');
      }
      onChange(saved);
      setStatus('ready');
    } catch (err) {
      setError(err?.message || 'Upload failed. Try again.');
    } finally {
      setProgress(false);
    }
  };

  const takeFiles = (list) => {
    const file = list?.[0];
    if (file) upload(file);
  };

  if (!url) {
    return (
      <div>
        <button
          type="button"
          disabled={progress}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            takeFiles(e.dataTransfer.files);
          }}
          className="flex h-28 w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 text-center"
        >
          <span className="text-sm text-gray-700">
            {progress ? 'Uploading…' : 'Drag & drop an image here'}
          </span>
          {!progress && (
            <span className="text-sm text-primary">or Choose image</span>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_TYPES.join(',')}
          className="sr-only"
          onChange={(e) => {
            takeFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <div className="flex h-28 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50">
        {(progress || status === 'loading') && (
          <span className="text-xs text-gray-500">
            {progress ? 'Uploading…' : 'Loading…'}
          </span>
        )}
        {status === 'error' && !progress && (
          <span className="px-2 text-center text-xs text-red-600">
            Image failed to load
          </span>
        )}
        {status !== 'error' && (
          <img
            src={url}
            alt=""
            className={`max-h-28 max-w-full object-contain ${status === 'ready' ? '' : 'hidden'}`}
            onLoad={() => setStatus('ready')}
            onError={() => setStatus('error')}
          />
        )}
      </div>
      <div className="mt-1.5 flex gap-2">
        <button
          type="button"
          className="h-8 rounded-md border border-gray-200 px-2.5 text-[13px] font-medium text-gray-700"
          onClick={() => inputRef.current?.click()}
          disabled={progress}
        >
          Replace
        </button>
        <button
          type="button"
          className="h-8 rounded-md border border-gray-200 px-2.5 text-[13px] font-medium text-red-600"
          onClick={() => onChange('')}
          disabled={progress}
        >
          Remove
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_TYPES.join(',')}
          className="sr-only"
          onChange={(e) => {
            takeFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function withCurrent(options, value) {
  if (!value || options.some((o) => o.value === value)) return options;
  return [{ value, label: value }, ...options];
}

export function EmailPropertyEditor({ selectedBlock, onUpdateBlock, onClose }) {
  const [advanced, setAdvanced] = useState(false);
  const panelRef = useRef(null);
  const blockId = selectedBlock?.id;
  useEffect(() => {
    setAdvanced(false);
  }, [blockId]);

  useEffect(() => {
    const vv = window.visualViewport;
    const node = panelRef.current;
    if (!vv || !node) return undefined;
    const fit = () => {
      node.style.maxHeight = `${Math.max(160, vv.height - 12)}px`;
    };
    fit();
    vv.addEventListener('resize', fit);
    return () => {
      vv.removeEventListener('resize', fit);
      if (node) node.style.maxHeight = '';
    };
  }, [blockId]);

  if (!selectedBlock) return null;
  const { type, content } = selectedBlock;
  const meta = BLOCKS.find((b) => b.type === type);
  const Icon = meta?.icon;

  const patch = (partial) =>
    onUpdateBlock(selectedBlock.id, { ...content, ...partial });
  const set = (key, value) => patch({ [key]: value });

  const quoteAccent =
    content.borderLeft?.match(/#[0-9a-fA-F]{3,8}/)?.[0] || '#50AB3D';
  const rich = ['HEADING', 'TEXT', 'QUOTE', 'FOOTER'].includes(type);
  const hasSpace = type !== 'SPACER';

  return (
    <div
      ref={panelRef}
      className="flex h-full min-h-0 w-full max-w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white font-sf-pro-display shadow-xl"
    >
      <style>{QUILL_CSS}</style>
      <header className="flex h-11 shrink-0 items-center gap-2 border-b border-gray-100 px-3">
        {Icon && (
          <span className="flex h-6 w-6 items-center justify-center rounded bg-surface-mint text-primary">
            <Icon size={14} />
          </span>
        )}
        <h3
          id="email-block-settings-title"
          className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900"
        >
          Edit {meta?.title || type}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-3 py-3">
        {rich && (
          <TextEditor
            blockId={selectedBlock.id}
            html={content.html}
            advanced={advanced}
            onChange={(html) => set('html', html)}
          />
        )}

        {type === 'QUOTE' && (
          <>
            <Field label="Background">
              <Swatches
                value={content.backgroundColor || '#f9fafb'}
                onChange={(v) => set('backgroundColor', v)}
                presets={[
                  '#ffffff',
                  '#f9fafb',
                  '#f0fdf4',
                  '#eff6ff',
                  '#fffbeb',
                  '#f3f4f6',
                ]}
              />
            </Field>
            <Field label="Accent">
              <Swatches
                value={quoteAccent}
                onChange={(v) => set('borderLeft', `4px solid ${v}`)}
              />
            </Field>
          </>
        )}

        {(type === 'IMAGE' || type === 'LOGO') && (
          <>
            <ImageSource url={content.url} onChange={(v) => set('url', v)} />
            <Field label="Link (optional)">
              <input
                className={inputClass}
                value={content.link || ''}
                placeholder="https://yourwebsite.com"
                onChange={(e) => set('link', e.target.value)}
              />
            </Field>
            <Field label="Image size">
              <CustomSelect
                value={content.width || (type === 'LOGO' ? '150px' : '100%')}
                onChange={(v) => set('width', v)}
                options={withCurrent(WIDTHS, content.width)}
                buttonClassName={selectBtn}
                textSizeClass="text-sm"
              />
            </Field>
            <AlignRow
              value={content.align || 'center'}
              onChange={(v) => set('align', v)}
            />
          </>
        )}

        {type === 'BUTTON' && (
          <>
            <Field label="Button text">
              <input
                className={inputClass}
                value={content.text || ''}
                onChange={(e) => set('text', e.target.value)}
              />
            </Field>
            <Field label="Link">
              <input
                className={inputClass}
                value={content.url || ''}
                placeholder="https://"
                onChange={(e) => set('url', e.target.value)}
              />
            </Field>
            <Field label="Background">
              <Swatches
                value={content.backgroundColor || '#50AB3D'}
                onChange={(v) => set('backgroundColor', v)}
              />
            </Field>
            <Field label="Text color">
              <Swatches
                value={content.textColor || '#ffffff'}
                onChange={(v) => set('textColor', v)}
                presets={[
                  '#ffffff',
                  '#1f2937',
                  '#000000',
                  '#f3f4f6',
                  '#50AB3D',
                ]}
              />
            </Field>
            <AlignRow
              value={content.align || 'center'}
              onChange={(v) => set('align', v)}
            />
          </>
        )}

        {type === 'SOCIAL' && (
          <>
            {(content.networks || []).map((net, idx) => (
              <div
                key={idx}
                className="flex min-w-0 flex-col gap-1.5 sm:flex-row sm:items-center"
              >
                <div className="w-full min-w-0 sm:w-[148px] sm:shrink-0">
                  <CustomSelect
                    value={net.platform}
                    onChange={(val) =>
                      set(
                        'networks',
                        content.networks.map((item, i) =>
                          i === idx ? { ...item, platform: val } : item,
                        ),
                      )
                    }
                    options={SOCIAL_PLATFORM_OPTIONS.map((option) => ({
                      ...option,
                      iconUrl:
                        option.value === net.platform
                          ? getSocialIconUrl(net.platform, net.iconUrl)
                          : getSocialIconUrl(option.value),
                    }))}
                    renderValue={(option) => (
                      <>
                        <img
                          src={option.iconUrl}
                          alt=""
                          className="h-[18px] w-[18px] shrink-0"
                        />
                        <span className="truncate">{option.label}</span>
                      </>
                    )}
                    renderOption={(option, { isSelected }) => (
                      <span className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm text-gray-900">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                          {isSelected && (
                            <svg
                              className="h-4 w-4 text-primary"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M4.5 12.75l6 6 9-13.5"
                              />
                            </svg>
                          )}
                        </span>
                        <img
                          src={option.iconUrl}
                          alt=""
                          className="h-[18px] w-[18px] shrink-0"
                        />
                        <span className="truncate">{option.label}</span>
                      </span>
                    )}
                    buttonClassName={selectBtn}
                    textSizeClass="text-sm"
                  />
                </div>
                <input
                  className={inputClass}
                  aria-label="Profile URL"
                  value={net.url === '#' ? '' : net.url || ''}
                  placeholder={getSocialPlaceholder(net.platform)}
                  onChange={(e) =>
                    set(
                      'networks',
                      content.networks.map((item, i) =>
                        i === idx ? { ...item, url: e.target.value } : item,
                      ),
                    )
                  }
                />
                <button
                  type="button"
                  aria-label="Remove"
                  className="h-9 shrink-0 px-1 text-xs font-medium text-red-600"
                  onClick={() =>
                    set(
                      'networks',
                      content.networks.filter((_, i) => i !== idx),
                    )
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="h-9 w-full rounded-md border border-dashed border-primary text-sm font-medium text-primary"
              onClick={() =>
                set('networks', [
                  ...(content.networks || []),
                  { platform: 'facebook', url: '' },
                ])
              }
            >
              Add link
            </button>
          </>
        )}

        {type === 'DIVIDER' && (
          <>
            <Field label="Color">
              <Swatches
                value={content.color || '#e5e7eb'}
                onChange={(v) => set('color', v)}
                presets={[
                  '#e5e7eb',
                  '#d1d5db',
                  '#9ca3af',
                  '#50AB3D',
                  '#1f2937',
                ]}
              />
            </Field>
            <div className="grid grid-cols-3 gap-1">
              {[
                ['1px', '1px'],
                ['2px', '2px'],
                ['4px', '4px'],
              ].map(([val, name]) => {
                const on = (content.height || '1px') === val;
                return (
                  <button
                    key={val}
                    type="button"
                    aria-pressed={on}
                    onClick={() => set('height', val)}
                    className={`flex h-9 flex-col items-center justify-center gap-1 rounded-md border text-xs ${
                      on
                        ? 'border-primary text-primary'
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    <span className="w-10 bg-current" style={{ height: val }} />
                    {name}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {type === 'SPACER' && (
          <Field label="Height">
            <div className="grid grid-cols-4 gap-1">
              {SPACER_HEIGHTS.map((p) => {
                const on = (content.height || '24px') === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => set('height', p.value)}
                    className={`h-9 rounded-md border text-[13px] font-medium ${
                      on
                        ? 'border-primary bg-surface-mint/50 text-primary'
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <input
              className={`${inputClass} mt-2`}
              aria-label="Custom height"
              value={content.height || '24px'}
              onChange={(e) => set('height', e.target.value)}
            />
          </Field>
        )}

        {hasSpace && <SpaceAround content={content} onChange={patch} />}

        <Advanced
          open={advanced}
          onToggle={() => setAdvanced((v) => !v)}
          show={rich || type === 'SOCIAL'}
        >
          {(type === 'IMAGE' || type === 'LOGO' || type === 'BUTTON') && (
            <Field label="Corners">
              <CustomSelect
                value={
                  content.borderRadius === '0'
                    ? '0px'
                    : content.borderRadius ||
                      (type === 'BUTTON' ? '8px' : '0px')
                }
                onChange={(v) => set('borderRadius', v)}
                options={withCurrent(
                  CORNERS,
                  content.borderRadius === '0' ? '0px' : content.borderRadius,
                )}
                buttonClassName={selectBtn}
                textSizeClass="text-sm"
              />
            </Field>
          )}
          {type === 'SOCIAL' && (
            <AlignRow
              value={content.align || 'center'}
              onChange={(v) => set('align', v)}
            />
          )}
          {type === 'SOCIAL' &&
            (content.networks || []).some((n) => n.platform === 'custom') &&
            (content.networks || []).map((net, idx) =>
              net.platform === 'custom' ? (
                <Field key={idx} label="Custom icon URL">
                  <input
                    className={inputClass}
                    value={net.iconUrl || ''}
                    placeholder="https://"
                    onChange={(e) =>
                      set(
                        'networks',
                        content.networks.map((item, i) =>
                          i === idx
                            ? { ...item, iconUrl: e.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                </Field>
              ) : null,
            )}
        </Advanced>
      </div>
    </div>
  );
}
